import { useState, useMemo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    TableSortLabel,
  } from '@mui/material';
  import { useDateSort } from '../../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../../components/common/TableActionsMenu';
  import { AdcashAvatyeDetailMetricsSummary } from '../../../../../types/adcash.types';
  
  type Props = {
    data: AdcashAvatyeDetailMetricsSummary;
    exchangeRate: number;
    onExchangeRateChange: (rate: number) => void;
  };
  
  export default function AdcashAvatyeDetailSummaryTable({ data, exchangeRate, onExchangeRateChange }: Props) {
    const dailyStats = data.dailyStats;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(dailyStats);

    const paged = useMemo(
        () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page]
    );

    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    const calculateFillRate = (req: number, imp: number) => {
        return req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';
    };

    const calculateCTR = (imp: number, clk: number) => {
        return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const formatNumberOnly = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

    const calculateECPM = useCallback((revenue: number, imp: number) => {
        return imp > 0 ? formatNumberOnly((revenue / imp) * 1000) : '0';
    }, [formatNumberOnly]);

    // 합계 계산
    const totals = useMemo(() => {
        const totalRevenue = sortedData.reduce((sum, row) => sum + row.revenue, 0) * exchangeRate;
        const totalReq = sortedData.reduce((sum, row) => sum + row.req, 0);
        const totalRes = sortedData.reduce((sum, row) => sum + row.res, 0);
        const totalImp = sortedData.reduce((sum, row) => sum + row.imp, 0);
        const totalClk = sortedData.reduce((sum, row) => sum + row.clk, 0);
        
        const avgFillRate = totalReq > 0 ? ((totalImp / totalReq) * 100).toFixed(2) : '0.00';
        const avgCTR = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
        const avgECPM = totalImp > 0 ? formatCurrency((totalRevenue / totalImp) * 1000) : formatCurrency(0);
        
        return {
            totalRevenue,
            totalReq,
            totalRes,
            totalImp,
            totalClk,
            avgFillRate,
            avgCTR,
            avgECPM
        };
    }, [sortedData, exchangeRate, formatCurrency]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시, 합계 행 제외)
    // 퍼센트 컬럼은 소수로 전달
    const allRowsDataForExcel = useMemo(() => {
        return sortedData.map((row) => {
            // fillRate와 CTR을 소수 형태로 (0.xx)
            const fillRate = row.req > 0 ? row.imp / row.req : 0;
            const ctr = row.imp > 0 ? row.clk / row.imp : 0;
            const ecpm = calculateECPM(row.revenue * exchangeRate, row.imp);
            return [
                row.date,
                formatNumberOnly(row.revenue * exchangeRate),
                `${row.topPlacement} (${row.topPlacementAppName})`,
                row.req.toLocaleString(),
                row.res.toLocaleString(),
                fillRate,
                row.imp.toLocaleString(),
                row.clk.toLocaleString(),
                ctr,
                ecpm
            ];
        });
    }, [sortedData, exchangeRate, formatNumberOnly, calculateECPM]);

    return (
        <Box>
            {/* 환율 버튼 1230 or 1350 */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1230, 1350].map(rate => (
                        <Button
                            key={rate}
                            size="small"
                            variant="outlined"
                            onClick={() => onExchangeRateChange(rate)}
                            sx={{
                                borderColor: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                color: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: exchangeRate === rate ? '#0044D2' : '#757575',
                                    backgroundColor: exchangeRate === rate ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                                }
                            }}
                        >
                            환율 {rate}
                        </Button>
                    ))}
                </Box>
                <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto'
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>매출</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      최고매출 지면 (앱)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Request</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Response</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Fill Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Impression</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Click</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>CTR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>eCPM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatCurrency(totals.totalRevenue)}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          -
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.totalReq.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.totalRes.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {totals.avgFillRate}%
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.totalImp.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.totalClk.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.avgCTR}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totals.avgECPM}</TableCell>
                    </TableRow>
                    {paged.map((row) => {
                        const fillRate = calculateFillRate(row.req, row.imp);
                        const ctr = calculateCTR(row.imp, row.clk);
                        const ecpm = calculateECPM(row.revenue * exchangeRate, row.imp);

                        return (
                            <TableRow key={row.date}>
                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.date}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(row.revenue * exchangeRate)}</TableCell>
                                <TableCell 
                                  align="right"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {row.topPlacement} ({row.topPlacementAppName})
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.req.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.res.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fillRate}%</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.imp.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.clk.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctr}%</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ecpm}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  